import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import GameCard from '../components/GameCard/GameCard';
import PullToRefreshIndicator from '../components/PullToRefreshIndicator/PullToRefreshIndicator';
import GameDetailsOverlay from '../components/GameDetailsOverlay/GameDetailsOverlay';
import GameDetailsOverlayMy from '../components/GameDetailsOverlayMy/GameDetailsOverlayMy';
import DailyQuestionBanner from '../components/DailyQuestionBanner/DailyQuestionBanner';

import DailyCalendar from '../components/DailyCalendar/DailyCalendar';
import DailyNoteWritingOverlay from '../components/DailyNoteWritingOverlay/DailyNoteWritingOverlay';
import { usePullToRefresh } from '../hooks/usePullToRefresh';
import { usePageCache } from '../hooks/usePageCache';
import {
    fetchLibraryGames,
    fetchMyGamesWithPagination,
    claimFreeGameBySlug
} from '../api/games';
import './HomePage.css';

import BackgroundSlideshow from '../components/BackgroundSlideshow/BackgroundSlideshow';

// Получаем "эффективную" дату (с учётом того, что до 3 утра считается предыдущий день)
const getEffectiveDate = (date = new Date()) => {
    const effectiveDate = new Date(date);
    if (effectiveDate.getHours() < 3) {
        effectiveDate.setDate(effectiveDate.getDate() - 1);
    }
    return effectiveDate;
};

// Глобальная переменная для хранения выбранной даты при навигации внутри сессии
let lastSelectedDate = null;

export default function HomePage({ onOpenGame = () => { } }) {
    const [myGames, setMyGames] = React.useState([]);
    const [recommendedGames, setRecommendedGames] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const [isInitialLoad, setIsInitialLoad] = React.useState(true);
    const [selectedGame, setSelectedGame] = React.useState(null);
    const [selectedGameSource, setSelectedGameSource] = React.useState(null); // 'my' or 'recommended'
    const [dailyNoteData, setDailyNoteData] = React.useState(null);

    // Инициализируем из глобальной переменной
    const [selectedDate, setSelectedDate] = React.useState(lastSelectedDate);

    // Сохраняем в глобальную переменную при изменении
    React.useEffect(() => {
        lastSelectedDate = selectedDate;
    }, [selectedDate]);
    const calendarRef = React.useRef(null);
    const dailyBannerRef = React.useRef(null);

    // Форматирование даты (используем эффективную дату для отображения)
    const getFormattedDate = () => {
        const date = selectedDate || getEffectiveDate();
        const dayName = new Intl.DateTimeFormat('ru-RU', { weekday: 'short' }).format(date);
        const capitalizedDayName = dayName.charAt(0).toUpperCase() + dayName.slice(1);
        const dayNumber = date.getDate();
        return `${capitalizedDayName}, ${dayNumber}`;
    };
    const formattedDate = getFormattedDate();

    const navigate = useNavigate();
    const { slug } = useParams();
    const { getCachedData } = usePageCache();

    // Функция загрузки данных для главной страницы
    const loadHomeData = React.useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            // Загружаем мои игры и все игры параллельно
            const [myGamesResult, allGamesResult] = await Promise.all([
                fetchMyGamesWithPagination({ page: 1, pageSize: 100, sort: 'created_desc' }),
                fetchLibraryGames({ page: 1, pageSize: 50, sort: 'created_desc' })
            ]);

            const myGamesItems = myGamesResult.items || [];
            const allGamesItems = allGamesResult.items || [];

            // Создаем Set из ID моих игр для быстрой проверки
            const myGameIds = new Set(myGamesItems.map(game => game.id));

            // Фильтруем рекомендуемые игры - исключаем те, что уже в моих играх
            const recommendedItems = allGamesItems.filter(game => !myGameIds.has(game.id));

            setMyGames(myGamesItems);
            setRecommendedGames(recommendedItems);

            return { myGames: myGamesItems, recommended: recommendedItems };
        } catch (e) {
            console.error('Error loading home data:', e);
            setError(e.message || String(e));
            throw e;
        } finally {
            setLoading(false);
        }
    }, []);

    // Используем кэш для первой загрузки
    const cachedData = getCachedData('home', loadHomeData, []);

    // Обновляем состояние из кэша при первой загрузке
    React.useEffect(() => {
        if (cachedData.data && isInitialLoad) {
            setMyGames(cachedData.data.myGames || []);
            setRecommendedGames(cachedData.data.recommended || []);
            setLoading(false);
            setIsInitialLoad(false);
        }
    }, [cachedData.data, isInitialLoad]);

    // Обновляем состояние загрузки из кэша
    React.useEffect(() => {
        if (cachedData.loading !== undefined) {
            setLoading(cachedData.loading);
        }
    }, [cachedData.loading]);

    // Обработка открытия игры через URL параметры (slug)
    React.useEffect(() => {
        if (slug) {
            // Ищем игру сначала в моих играх
            const myGame = myGames.find(g => g.slug === slug);
            if (myGame) {
                const modalData = transformGameForModal(myGame, true);
                setSelectedGame(modalData);
                setSelectedGameSource('my');
                return;
            }

            // Затем в рекомендуемых
            const recommendedGame = recommendedGames.find(g => g.slug === slug);
            if (recommendedGame) {
                const modalData = transformGameForModal(recommendedGame, false);
                setSelectedGame(modalData);
                setSelectedGameSource('recommended');
            }
        }
    }, [slug, myGames, recommendedGames]);

    // Если базовый путь без slug — убеждаемся, что модалка закрыта
    React.useEffect(() => {
        if (!slug && selectedGame) {
            setSelectedGame(null);
            setSelectedGameSource(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug]);

    // Функция для pull-to-refresh
    const handleRefresh = React.useCallback(async () => {
        setSelectedDate(null);
        await Promise.all([
            cachedData.refresh(),
            calendarRef.current?.refreshCalendarData()
        ]);
    }, [cachedData]);

    // Настройка pull-to-refresh
    const {
        elementRef,
        isRefreshing,
        isPulling,
        pullDistance,
        refreshProgress,
        canRefresh
    } = usePullToRefresh(handleRefresh, {
        threshold: 80,
        resistance: 2.5,
        maxPullDistance: 120,
        enabled: !loading && !error
    });

    async function onClaim(slug) {
        try {
            await claimFreeGameBySlug(slug);
            // Обновим список
            await loadHomeData();
        } catch (e) {
            alert(e.message || String(e));
        }
    }

    // Преобразуем данные из Supabase в формат для GameCard
    const transformGameForCard = (game, isMyGame = false) => {
        const isFree = (game.price_cents || 0) <= 0;

        if (isMyGame) {
            return {
                id: game.id,
                title: game.title,
                subtitle: game.subtitle,
                coverUrl: game.coverUrl,
                status: game.access_via === 'purchase' ? 'owned' : (isFree ? 'free' : 'paid'),
                slug: game.slug,
                priceCents: game.price_cents || 0,
                currency: game.currency || 'RUB',
                isFree,
                subscriptionIncluded: game.included_in_subscription,
                accessVia: game.access_via,
                cardCount: game.card_count || 0
            };
        }

        return {
            id: game.id,
            title: game.title,
            subtitle: game.subtitle,
            coverUrl: game.coverUrl,
            status: isFree ? 'free' : 'paid',
            slug: game.slug,
            priceCents: game.price_cents || 0,
            currency: game.currency || 'RUB',
            isFree,
            subscriptionIncluded: game.included_in_subscription,
            cardCount: game.card_count || 0
        };
    };

    // Преобразуем данные для модалки с полной обложкой
    const transformGameForModal = (game, isMyGame = false) => {
        const isFree = (game.price_cents || 0) <= 0;
        return {
            id: game.id,
            title: game.title,
            subtitle: game.subtitle,
            description: game.description,
            coverUrl: game.fullCoverUrl,
            status: isFree ? 'free' : 'paid',
            slug: game.slug,
            priceCents: game.price_cents || 0,
            currency: game.currency || 'RUB',
            isFree,
            subscriptionIncluded: game.included_in_subscription,
            ozonLink: game.ozon_link || null,
            isMyGame,
            accessVia: game.access_via,
            cardCount: game.card_count || 0
        };
    };

    // Обработчик открытия игры - открываем модалку
    const handleOpenGame = (game, isMyGame = false) => {
        const sourceGames = isMyGame ? myGames : recommendedGames;
        const originalGame = sourceGames.find(g => g.id === game.id);
        if (originalGame) {
            const modalData = transformGameForModal(originalGame, isMyGame);
            setSelectedGame(modalData);
            setSelectedGameSource(isMyGame ? 'my' : 'recommended');
            navigate(`/home/${originalGame.slug}`);
        }
    };

    // Обработчики для модалки
    const handleCloseGame = () => {
        setSelectedGame(null);
        setSelectedGameSource(null);
        navigate('/home');
    };

    const handleShare = () => {
        // TODO: Реализовать функционал шаринга
    };

    const handlePlay = () => {
        if (selectedGame) {
            navigate(`/game/${selectedGame.id}`);
        }
    };

    const handleOpenNote = (card, gameId) => {
        setDailyNoteData({ card, gameId });
    };

    const handleCloseNote = () => {
        setDailyNoteData(null);
    };

    // Скелетон для загрузки
    if (loading) return (
        <main className="page">
            <div className="home-header">
                <h1>Главная</h1>
                <div className="date-display">
                    <span>{formattedDate}</span>
                    <img src="/icons/calendar.svg" alt="Календарь" />
                </div>
            </div>

            {/* Скелетон для баннера */}
            <div className="skeleton-card" style={{ height: '200px', borderRadius: '32px', marginBottom: '24px' }} />

            {/* Скелетон для 12-24 карточек */}
            <div className="game-list">
                {Array.from({ length: 12 }, (_, i) => (
                    <div key={i}>
                        <div className="skeleton-card" style={{ height: '100px' }} />
                        {i !== 11 && <div className="divider" />}
                    </div>
                ))}
            </div>
        </main>
    );

    // Обработка ошибок
    if (error) return (
        <main className="page">
            <div className="home-header">
                <h1>Главная</h1>
                <div className="date-display">
                    <span>{formattedDate}</span>
                    <img src="/icons/calendar.svg" alt="Календарь" />
                </div>
            </div>
            <div style={{ textAlign: 'center', padding: '2rem', color: 'red' }}>
                <h3>Ошибка загрузки данных</h3>
                <p>{error}</p>
                <button
                    onClick={() => loadHomeData()}
                    style={{ marginTop: '1rem', padding: '0.5rem 1rem' }}
                >
                    Попробовать снова
                </button>
            </div>
        </main>
    );

    // Проверяем, нужно ли показывать секции
    const showMyGames = myGames.length > 0;
    const showRecommended = recommendedGames.length > 0;

    return (
        <>
            <PullToRefreshIndicator
                isRefreshing={isRefreshing}
                isPulling={isPulling}
                progress={refreshProgress}
                canRefresh={canRefresh}
                pullDistance={pullDistance}
            />
            <main className="page home-page" ref={elementRef}>
                <div className="home-top-content">
                    {/* Background Video (now Slideshow) */}
                    <div className="home-background-video">
                        <BackgroundSlideshow />
                    </div>

                    <div className="home-header">
                        <h1>Главная</h1>
                        <div
                            className="date-display"
                            onClick={() => calendarRef.current?.toggleView()}
                            style={{ cursor: 'pointer' }}
                        >
                            <span>{formattedDate}</span>
                            <img
                                src="/icons/calendar.svg"
                                alt="Календарь"
                            />
                        </div>
                    </div>

                    {/* Баннер "Вопрос дня" */}
                    <DailyQuestionBanner
                        ref={dailyBannerRef}
                        onOpenNote={handleOpenNote}
                        onQuestionOpened={() => calendarRef.current?.refreshCalendarData()}
                        selectedDate={selectedDate}
                        onDateChange={setSelectedDate}
                    />

                    {/* Календарь */}
                    <DailyCalendar
                        ref={calendarRef}
                        onDayClick={setSelectedDate}
                        selectedDate={selectedDate}
                    />
                </div>

                <div className="home-bottom-sheet">
                    {/* Секция "Мои игры" - показываем только если есть игры */}
                    {showMyGames && (
                        <>
                            <h2>Мои игры</h2>
                            <section className="game-list">
                                {myGames.map((game, idx) => {
                                    const cardData = transformGameForCard(game, true);
                                    return (
                                        <div key={game.id}>
                                            <GameCard
                                                {...cardData}
                                                onlyAccessVia
                                                onClick={() => handleOpenGame(cardData, true)}
                                            />
                                            {idx !== myGames.length - 1 && <div className="divider" />}
                                        </div>
                                    );
                                })}
                            </section>
                        </>
                    )}

                    {/* Секция "Рекомендуем" - показываем только если есть игры */}
                    {showRecommended && (
                        <>
                            <h2>Рекомендуем</h2>
                            <section className="game-list">
                                {recommendedGames.map((game, idx) => {
                                    const cardData = transformGameForCard(game, false);
                                    return (
                                        <div key={game.id}>
                                            <GameCard
                                                {...cardData}
                                                onClick={() => handleOpenGame(cardData, false)}
                                                onClaim={onClaim}
                                            />
                                            {idx !== recommendedGames.length - 1 && <div className="divider" />}
                                        </div>
                                    );
                                })}
                            </section>
                        </>
                    )}

                    {/* Пустое состояние - если нет ни моих игр, ни рекомендаций */}
                    {!showMyGames && !showRecommended && (
                        <div className="empty-state">
                            <p>Нет доступных игр</p>
                        </div>
                    )}
                </div>
            </main>

            {/* Модалка с деталями игры */}
            {selectedGame && selectedGameSource === 'my' && (
                <GameDetailsOverlayMy
                    game={selectedGame}
                    onClose={handleCloseGame}
                    onShare={handleShare}
                    onPlay={handlePlay}
                />
            )}

            {selectedGame && selectedGameSource === 'recommended' && (
                <GameDetailsOverlay
                    game={selectedGame}
                    onClose={handleCloseGame}
                    onShare={handleShare}
                    onPlay={handlePlay}
                />
            )}

            {/* Оверлей написания заметки для вопроса дня */}
            {dailyNoteData && (
                <DailyNoteWritingOverlay
                    gameId={dailyNoteData.gameId}
                    card={dailyNoteData.card}
                    onClose={handleCloseNote}
                    onSave={(text) => {
                        console.log('Note saved:', text);
                        // Обновляем календарь после сохранения заметки
                        calendarRef.current?.refreshCalendarData();
                        // Обновляем баннер, чтобы показать "Посмотреть ответ"
                        dailyBannerRef.current?.markAsAnswered();
                    }}
                />
            )}
        </>
    );
}
